// Admin > Episodes tab — RSS-sourced episode list with an editorial layer.
// Click any row to enrich with show notes, subtitle, meta description, OG
// image, chapters, and guest names. Data flows: RSS is source of truth for
// audio/pub_date; this tab overlays admin fields on top.
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Edit3, Check, Plus, Trash2, ExternalLink, ClipboardPaste } from "lucide-react";
import { fmt } from "./_helpers";


/**
 * Parse a block of pasted text into chapters. Accepts common formats:
 *   00:00 Intro
 *   2:15 Guest introduction
 *   15:40 - Building topline strength
 *   1:02:10 – Q&A
 *   [15:40] Chapter label
 * Ignores blank lines and lines that don't lead with a timestamp so a full
 * copy-paste from Descript / a Google Doc still works.
 * Returns [{time, label}] with times normalised to "MM:SS" or "HH:MM:SS".
 */
function parseChaptersText(raw) {
  const out = [];
  const lineRe = /^\s*\[?(\d{1,2}(?::\d{1,2}){1,2})\]?\s*[-–—:.\)]?\s*(.+?)\s*$/;
  for (const line of (raw || "").split(/\r?\n/)) {
    const m = line.match(lineRe);
    if (!m) continue;
    const label = (m[2] || "").trim();
    if (!label) continue;
    // Normalise: strip leading "0:" on 3-part times, pad 2-digit seconds/minutes
    const parts = m[1].split(":").map((p) => p.padStart(2, "0"));
    let time;
    if (parts.length === 3) {
      // HH:MM:SS — drop leading "00:" so short episodes read cleanly
      time = parts[0] === "00" ? `${parts[1]}:${parts[2]}` : parts.join(":");
    } else {
      time = parts.join(":");
    }
    out.push({ time, label });
  }
  return out;
}


function EpisodeEditor({ open, onOpenChange, guid, onSaved }) {
  const [state, setState] = useState({ loading: false, episode: null, form: null });
  const [saving, setSaving] = useState(false);
  // Bulk-paste chapter panel state — collapsed by default; opens beneath the
  // Chapters header when the user clicks "Paste chapters".
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const load = useCallback(async () => {
    if (!guid) return;
    setState({ loading: true, episode: null, form: null });
    try {
      const { data } = await api.get(`/admin/podcast/episodes/${encodeURIComponent(guid)}`);
      const ov = data.override || {};
      setState({
        loading: false,
        episode: data.episode,
        form: {
          title_override: ov.title_override || "",
          subtitle: ov.subtitle || "",
          show_notes_md: ov.show_notes_md || "",
          transcript: ov.transcript || "",
          meta_description: ov.meta_description || "",
          og_image_url: ov.og_image_url || "",
          // Stable UI-only key per chapter row so React can track rows when
          // reordering/removing. Stripped before we POST to the server.
          chapters: (ov.chapters || []).map((c) => ({ ...c, _k: crypto.randomUUID?.() || `${c.time}-${c.label}-${Math.random()}` })),
          guest_names: (ov.guest_names || []).join(", "),
          published_slug_override: ov.published_slug_override || "",
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load episode");
      onOpenChange(false);
    }
  }, [guid, onOpenChange]);

  useEffect(() => {
    if (open && guid) load();
  }, [open, guid, load]);

  const setField = (k, v) => setState((s) => ({ ...s, form: { ...s.form, [k]: v } }));

  const addChapter = () => setField("chapters", [
    ...(state.form?.chapters || []),
    { _k: crypto.randomUUID?.() || `new-${Date.now()}-${Math.random()}`, time: "00:00", label: "" },
  ]);
  const removeChapter = (idx) => setField("chapters", state.form.chapters.filter((_, i) => i !== idx));
  const patchChapter = (idx, patch) => setField(
    "chapters",
    state.form.chapters.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
  );

  // Bulk paste handlers. Two paths:
  //   - "Append" adds parsed rows below whatever's already there
  //   - "Replace all" swaps everything out (nice for re-doing an episode)
  const applyPastedChapters = (mode) => {
    const parsed = parseChaptersText(pasteText);
    if (parsed.length === 0) {
      toast.error("No chapters found — expected lines like '00:00 Intro'");
      return;
    }
    const withKeys = parsed.map((p) => ({
      ...p,
      _k: crypto.randomUUID?.() || `paste-${Date.now()}-${Math.random()}`,
    }));
    const next = mode === "replace"
      ? withKeys
      : [...(state.form?.chapters || []), ...withKeys];
    setField("chapters", next);
    setPasteText("");
    setPasteOpen(false);
    toast.success(`${parsed.length} chapter${parsed.length === 1 ? "" : "s"} added`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title_override: state.form.title_override || null,
        subtitle: state.form.subtitle || null,
        show_notes_md: state.form.show_notes_md || null,
        transcript: state.form.transcript || null,
        meta_description: state.form.meta_description || null,
        og_image_url: state.form.og_image_url || null,
        published_slug_override: state.form.published_slug_override || null,
        chapters: state.form.chapters
          .filter((c) => c.label.trim())
          .map(({ _k, ...rest }) => rest),   // strip UI-only key before POST
        guest_names: state.form.guest_names
          ? state.form.guest_names.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await api.put(`/admin/podcast/episodes/${encodeURIComponent(guid)}`, payload);
      toast.success("Episode saved");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="episode-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit episode</DialogTitle>
          <DialogDescription>
            RSS-sourced fields (audio, pub date, duration) stay linked to Anchor. Everything below is your editorial layer.
          </DialogDescription>
        </DialogHeader>

        {state.loading || !state.form ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-5" data-testid="episode-editor-form">
            {/* RSS ref block */}
            <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
              <p><span className="font-semibold text-foreground">RSS title:</span> {state.episode.rss_title}</p>
              <p className="mt-1"><span className="font-semibold text-foreground">Slug:</span> /podcast/{state.episode.slug}</p>
              <p className="mt-1"><span className="font-semibold text-foreground">Published:</span> {state.episode.pub_date} · {state.episode.duration}</p>
            </div>

            <div>
              <Label htmlFor="ep-title">Title override</Label>
              <Input
                id="ep-title"
                value={state.form.title_override}
                onChange={(e) => setField("title_override", e.target.value)}
                placeholder="Leave blank to use RSS title"
                data-testid="ep-title-override"
              />
            </div>

            <div>
              <Label htmlFor="ep-subtitle">Subtitle / hook</Label>
              <Input
                id="ep-subtitle"
                value={state.form.subtitle}
                onChange={(e) => setField("subtitle", e.target.value)}
                placeholder="One-line teaser for social + SERP"
                data-testid="ep-subtitle"
              />
            </div>

            <div>
              <Label htmlFor="ep-notes">Show notes (markdown)</Label>
              <Textarea
                id="ep-notes"
                value={state.form.show_notes_md}
                onChange={(e) => setField("show_notes_md", e.target.value)}
                rows={10}
                placeholder="## Topics&#10;- Grooming primer&#10;- Guest links: [Bobbie Lyons](https://example.com)&#10;&#10;## Timestamps&#10;- 00:00 Cold open&#10;- 02:14 Interview begins"
                className="font-mono text-sm"
                data-testid="ep-shownotes"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Supports **bold**, *italic*, [links](https://…), and - bullet lists. Google indexes this text — put your best keywords early.
              </p>
            </div>

            <div>
              <Label htmlFor="ep-transcript">Transcript (full episode text)</Label>
              <Textarea
                id="ep-transcript"
                value={state.form.transcript}
                onChange={(e) => setField("transcript", e.target.value)}
                rows={12}
                placeholder="Paste the full episode transcript here.&#10;&#10;Speaker labels and timestamps are welcome:&#10;&#10;[00:00] Host: Welcome to the Poodle People Podcast…&#10;[00:42] Guest: Thanks for having me…"
                className="font-mono text-xs"
                data-testid="ep-transcript"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Displayed below the show notes on the public episode page and indexed by Google for search visibility. {state.form.transcript ? `${state.form.transcript.length.toLocaleString()} characters` : "Not yet published"}.
              </p>
            </div>

            <div>
              <Label htmlFor="ep-meta">Meta description (SEO)</Label>
              <Textarea
                id="ep-meta"
                value={state.form.meta_description}
                onChange={(e) => setField("meta_description", e.target.value)}
                rows={3}
                maxLength={200}
                placeholder="One-sentence summary shown in Google results (155 chars max). If blank, we use the subtitle or RSS description."
                data-testid="ep-meta-description"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{state.form.meta_description.length} / 155 characters recommended</p>
            </div>

            <div>
              <Label htmlFor="ep-og">Social preview image URL</Label>
              <Input
                id="ep-og"
                value={state.form.og_image_url}
                onChange={(e) => setField("og_image_url", e.target.value)}
                placeholder="Leave blank to reuse the episode cover"
                data-testid="ep-og-image"
              />
            </div>

            <div>
              <Label htmlFor="ep-guests">Guest names (comma-separated)</Label>
              <Input
                id="ep-guests"
                value={state.form.guest_names}
                onChange={(e) => setField("guest_names", e.target.value)}
                placeholder="Bobbie Lyons, Jane Doe"
                data-testid="ep-guest-names"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <Label>Chapters</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPasteOpen((v) => !v)}
                    data-testid="ep-paste-chapters-toggle"
                  >
                    <ClipboardPaste size={12} className="mr-1" />
                    {pasteOpen ? "Cancel paste" : "Paste chapters"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addChapter} data-testid="ep-add-chapter">
                    <Plus size={12} className="mr-1" /> Add
                  </Button>
                </div>
              </div>

              {pasteOpen && (
                <div className="mb-3 rounded-md border border-border bg-muted/30 p-3" data-testid="ep-paste-chapters-panel">
                  <Textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={7}
                    placeholder={"Paste your chapter list. One per line:\n\n00:00 Intro\n2:15 Guest introduction\n15:40 - Building topline strength\n1:02:10 Q&A"}
                    className="font-mono text-sm"
                    data-testid="ep-paste-chapters-textarea"
                    autoFocus
                  />
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Recognises <code>MM:SS</code> and <code>HH:MM:SS</code> timestamps, separators <code>-</code> / <code>–</code> / <code>:</code>, and optional <code>[brackets]</code>. Non-timestamp lines are ignored.
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => applyPastedChapters("append")}
                      disabled={!pasteText.trim()}
                      data-testid="ep-paste-chapters-append"
                    >
                      Add to list
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyPastedChapters("replace")}
                      disabled={!pasteText.trim()}
                      data-testid="ep-paste-chapters-replace"
                    >
                      Replace all
                    </Button>
                  </div>
                </div>
              )}

              {state.form.chapters.length === 0 && !pasteOpen ? (
                <p className="text-xs text-muted-foreground">No chapters yet. Add a few for better SEO + accessibility.</p>
              ) : state.form.chapters.length > 0 ? (
                <div className="space-y-2">
                  {state.form.chapters.map((c, i) => (
                    <div key={c._k} className="flex items-center gap-2" data-testid={`ep-chapter-${i}`}>
                      <Input
                        value={c.time}
                        onChange={(e) => patchChapter(i, { time: e.target.value })}
                        placeholder="00:00"
                        className="w-24 font-mono text-sm"
                      />
                      <Input
                        value={c.label}
                        onChange={(e) => patchChapter(i, { label: e.target.value })}
                        placeholder="Chapter label"
                        className="flex-1"
                      />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeChapter(i)} data-testid={`ep-remove-chapter-${i}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <details>
              <summary className="text-xs text-muted-foreground cursor-pointer">Advanced: custom slug</summary>
              <div className="mt-2">
                <Input
                  value={state.form.published_slug_override}
                  onChange={(e) => setField("published_slug_override", e.target.value)}
                  placeholder="Only set if you want a slug different from '{n}-{title}'"
                  data-testid="ep-slug-override"
                />
              </div>
            </details>

            <a
              href={`/podcast/${state.episode.slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              data-testid="ep-preview-link"
            >
              Preview public page <ExternalLink size={11} />
            </a>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ep-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || state.loading} data-testid="ep-save">
            <Check size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function EpisodesTab() {
  const [state, setState] = useState({ loading: true, episodes: [] });
  const [editorFor, setEditorFor] = useState(null);   // guid string when open
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/podcast/episodes");
      setState({ loading: false, episodes: data.episodes || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load episodes");
      setState({ loading: false, episodes: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/podcast/refresh");
      toast.success("RSS refreshed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-episodes">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          {state.loading ? "Loading…" : `${state.episodes.length} episodes from RSS. ${state.episodes.filter((e) => e.has_notes).length} enriched with show notes.`}
        </p>
        <Button variant="outline" size="sm" onClick={forceRefresh} disabled={refreshing} data-testid="episodes-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh from Anchor"}
        </Button>
      </div>

      <Card className="border-border shadow-none rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {state.episodes.map((ep) => (
              <li key={ep.guid} className="flex items-start gap-4 p-4" data-testid={`episode-row-${ep.slug}`}>
                {ep.image ? (
                  <img src={ep.image} alt="" className="w-14 h-14 rounded object-cover shrink-0" loading="lazy" />
                ) : null}
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-snug">{ep.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-mono">/podcast/{ep.slug}</span>
                    {ep.duration ? ` · ${ep.duration}` : ""}
                    {ep.pub_date ? ` · ${ep.pub_date.split(",")[1]?.trim().split(" ").slice(0, 3).join(" ") || ep.pub_date}` : ""}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ${
                        ep.has_notes
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      }`}
                      data-testid={`episode-status-${ep.slug}`}
                    >
                      {ep.has_notes ? "Notes ready" : "Needs notes"}
                    </span>
                    {ep.guest_names?.length ? (
                      <span className="text-[11px] text-muted-foreground">
                        with {ep.guest_names.slice(0, 2).join(", ")}
                        {ep.guest_names.length > 2 ? "…" : ""}
                      </span>
                    ) : null}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditorFor(ep.guid)}
                  data-testid={`episode-edit-${ep.slug}`}
                >
                  <Edit3 size={13} className="mr-1.5" /> Edit
                </Button>
              </li>
            ))}
            {!state.loading && state.episodes.length === 0 ? (
              <li className="p-10 text-center text-sm text-muted-foreground">
                No episodes in the feed yet.
              </li>
            ) : null}
          </ul>
        </CardContent>
      </Card>

      <EpisodeEditor
        open={!!editorFor}
        onOpenChange={(v) => { if (!v) setEditorFor(null); }}
        guid={editorFor}
        onSaved={load}
      />

      <p className="text-[11px] text-muted-foreground">
        Sitemap: <code>{`{origin}`}/api/sitemap.xml</code> — submit that URL to Google Search Console once you&apos;ve enriched a few episodes.
      </p>
    </div>
  );
}
